import { LicenseInfo } from '@mui/x-data-grid-pro';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import moment from 'moment-timezone';
import { shape, string } from 'prop-types';
import React, { useContext, useEffect } from 'react';
import { Route, Switch } from 'react-router-dom';
import { Slide, ToastContainer } from 'react-toastify';
import appConfig from 'appConfig';
import _ from 'lodash';

import { PrivateRoute, PublicRoute } from './components/AuthenticatedRoutes';
import {
  Application,
  Authentication,
  Initialization,
  Join,
  PrivateCallback,
  PublicCallback,
  Setup,
  VerifyEmail,
} from './scenes';
import { MfaEnroll, MfaLogin } from './scenes/Authentication/components';
import sessionUtils from './services/auth/utils/sessionUtils';
import { CommandAiContext } from './providers/CommandAi';
import { resetOnboardingChecklist } from './services/commandAi/commandAi';
import { GlobalStyles } from './global-styles/components';
import { AUTH_SEARCH_PARAMS } from './scenes/Authentication/components/authFormUtils';
import { useQueryParams } from './providers/QueryParams';
import authenticateAndRedirectToHappyFox from './services/happyFox/happyFox';
import utils from './utils';


LicenseInfo.setLicenseKey(process.env.MUI_X_PRO_LICENSE_KEY);

const App = ({
  appId, appOrgId, session, organization,
}) => {
  const isAuthenticated = sessionUtils.isAuthenticated(session);
  const { userContext } = session;
  const { featureSetId: orgFeatureSetId, id: organizationId } = organization;
  const { isBooted, setIsBooted, shutDownCommandAi } = useContext(CommandAiContext);
  const { params, clearQueryParams } = useQueryParams();

  const ldClient = useLDClient();
  useEffect(() => {
    if (ldClient && isAuthenticated) {
      ldClient.identify({
        kind: 'multi',
        user: {
          key: session.userContext.dashboardUserId,
          name: utils.getFullName(session.userContext.firstName, session.userContext.lastName),
          email: session.userContext.email,
        },
        // conditionally identify because a user may not have an org or app yet
        ...(appId && appOrgId ? {
          app: {
            key: appId,
            organizationId: appOrgId,
          },
        } : {}),
        ...(organizationId && orgFeatureSetId ? {
          team: {
            key: organizationId,
            featureSetId: orgFeatureSetId,
            createdAt: moment(organization.createdAt).valueOf(),
          },
        } : {}),
      });
    }
  }, [isAuthenticated, appId, organizationId]);

  useEffect(() => {
    if (isAuthenticated) {
      window.CommandBar.boot(
        userContext.dashboardUserId,
        {
          email: userContext.email,
          firstName: userContext.firstName,
          lastName: userContext.lastName,
          env: process.env.NODE_ENV,
          baseURL: _.get(appConfig, 'baseURL'),
          createdAt: userContext.createdAt,
          daysSinceUserCreation: moment().diff(userContext.createdAt, 'days'),
          emailVerifiedAt: userContext.emailVerifiedAt,
          lastSuccessfulLogin: userContext.lastSuccessfulLoginAt,
          jobTitle: userContext.jobTitle,
          loginProvider: userContext.loginProvider,
          industry: userContext.industry,
          mfaEnabled: Boolean(userContext.mfaFactorId),
          experiment: userContext.experiment,
        },
        { hmac: userContext.commandAIHmac },
      ).then(() => {
        setIsBooted(true);
      });
    }

    return () => {
      setIsBooted(false);
      shutDownCommandAi();
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (
      params &&
      Object.keys(params).length > 0 &&
      Object.keys(params).includes(AUTH_SEARCH_PARAMS.HAPPY_FOX) &&
      isAuthenticated &&
      organizationId
    ) {
      authenticateAndRedirectToHappyFox(clearQueryParams, organizationId);
    }
  }, [params, isAuthenticated, organizationId]);

  useEffect(() => {
    // addresses edge case caused by race condition where shutdown occurs before booting is complete
    if (isBooted && !isAuthenticated) {
      setIsBooted(false);
      shutDownCommandAi();
    }
  }, [isBooted, isAuthenticated]);

  useEffect(() => {
    resetOnboardingChecklist();
  }, [organizationId]);

  useEffect(() => {
    if (isBooted) {
      const commandBar = document.getElementById('commandbar');
      commandBar.style.display = window.location.pathname.includes('/setup') ? 'none' : 'block';
    }
  }, [isBooted, window.location.pathname.includes('/setup')]);

  return (
    <div>
      <ToastContainer transition={Slide} />
      <Switch>
        <Route
          component={Join}
          path="/join"
        />
        <PublicRoute
          component={PublicCallback}
          isAuthenticated={isAuthenticated}
          path="/callback"
          redirect="/"
        />
        <PublicRoute
          component={Authentication}
          isAuthenticated={isAuthenticated}
          path="/login"
          redirect="/"
        />
        <PublicRoute
          component={Authentication}
          isAuthenticated={isAuthenticated}
          path="/signup"
          redirect="/"
        />
        <PublicRoute
          component={Authentication}
          isAuthenticated={isAuthenticated}
          path="/forgot-password"
          redirect="/"
        />
        <PublicRoute
          component={Authentication}
          isAuthenticated={false}
          path="/reset-password"
          redirect="/"
        />
        <PublicRoute
          component={MfaLogin}
          isAuthenticated={isAuthenticated}
          path="/mfa-login"
          redirect="/"
        />
        <PublicRoute
          component={MfaEnroll}
          isAuthenticated={isAuthenticated}
          path="/mfa-enroll"
          redirect="/"
        />
        <PrivateRoute
          component={VerifyEmail}
          isAuthenticated={isAuthenticated}
          path="/verify-email"
          redirect="/login"
        />
        <PrivateRoute
          component={Setup}
          isAuthenticated={isAuthenticated}
          path="/setup"
          redirect="/login"
        />
        <PrivateRoute
          component={Application}
          isAuthenticated={isAuthenticated}
          path="/apps/:applicationId"
          redirect="/login"
        />
        <PrivateRoute
          component={Application}
          isAuthenticated={isAuthenticated}
          path="/team"
          redirect="/login"
        />
        <PrivateRoute
          component={Application}
          isAuthenticated={isAuthenticated}
          path="/user"
          redirect="/login"
        />
        <PrivateRoute
          component={Initialization}
          isAuthenticated={isAuthenticated}
          path="/apps"
          redirect="/login"
        />
        <PrivateRoute
          component={PrivateCallback}
          isAuthenticated={isAuthenticated}
          path="/private-callback"
          redirect="/login"
        />
        <PrivateRoute
          component={Initialization}
          isAuthenticated={isAuthenticated}
          path="/"
          redirect="/login"
        />
      </Switch>
      <GlobalStyles />
    </div>
  );
};

export default App;

App.propTypes = {
  appId: string,
  appOrgId: string,
  session: shape({
    userContext: shape({
      firstName: string,
      lastName: string,
      email: string,
      dashboardUserId: string,
    }),
  }).isRequired,
  organization: shape({
    featureSetId: string,
  }),
};

App.defaultProps = {
  appId: null,
  appOrgId: null,
  organization: {},
};
